import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import {
  EmptyOrders,
  FilterTabs,
  StatusBadge,
  formatPrice,
} from "../shared/OrdersTablePrimitives";

const TABS = [
  { id: "all", label: "All" },
  { id: "completed", label: "Completed" },
  { id: "pending", label: "Pending" },
];

const CreatorOrders = ({ orders }) => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState({});

  const toggleExpand = (orderId) => {
    setExpanded((prev) => ({ ...prev, [orderId]: !prev[orderId] }));
  };

  const flattenedOrders = useMemo(() => {
    if (!orders || Object.keys(orders).length === 0) return [];
    return Object.values(orders)
      .flatMap((statusOrders) =>
        statusOrders ? Object.values(statusOrders) : []
      )
      .filter((order) => order !== undefined)
      .sort((a,b) => {
        const dateA = new Date(a.dueDate || a.timestamp || 0);
        const dateB = new Date(b.dueDate || b.timestamp || 0);
        return dateB-dateA;
      })
  }, [orders]);

  const filteredOrders = useMemo(() => {
    if (filter === "all") return flattenedOrders;
    return flattenedOrders.filter((order) => order.status === filter);
  }, [filter, flattenedOrders]);

  const openOrder = (order) => navigate(`/order/${order.orderNumber}`);

  return (
    <div className="space-y-5">
      <FilterTabs tabs={TABS} value={filter} onChange={setFilter} />

      {filteredOrders.length === 0 ? (
        <EmptyOrders />
      ) : (
        <>
          <div className="hidden overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-xl lg:block">
            <div className="grid grid-cols-[1.1fr_2fr_1fr_0.8fr_0.9fr_1fr_0.7fr] gap-3 border-b border-white/10 px-5 py-3 text-[10px] font-semibold uppercase tracking-wider text-white/40">
              <div>Order</div>
              <div>Title</div>
              <div>Due</div>
              <div>Days</div>
              <div>Price</div>
              <div>Status</div>
              <div className="text-right">Action</div>
            </div>

            {filteredOrders.map((order, index) => (
              <div
                key={order.orderNumber || index}
                className="grid grid-cols-[1.1fr_2fr_1fr_0.8fr_0.9fr_1fr_0.7fr] items-center gap-3 border-b border-white/5 px-5 py-4 text-sm transition last:border-b-0 hover:bg-white/[0.04]"
              >
                <button
                  type="button"
                  onClick={() => openOrder(order)}
                  className="truncate text-left font-mono text-xs text-white hover:text-emerald-300"
                >
                  #{order.orderNumber}
                </button>

                <div className="min-w-0">
                  <p className="truncate font-semibold text-white">
                    {order.title || "Untitled"}
                  </p>
                  {order.description && (
                    <p className="truncate text-xs text-white/40">
                      {order.description}
                    </p>
                  )}
                </div>

                <div className="text-xs text-white/70">{order.dueDate || "-"}</div>
                <div className="text-xs text-white/70">
                  {order.daysNeeded ?? "-"}
                </div>
                <div className="font-semibold text-white">
                  {formatPrice(order.price?.initial)}
                </div>
                <div>
                  <StatusBadge status={order.status} />
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => openOrder(order)}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/80 transition hover:bg-white/10 hover:text-white"
                  >
                    View <ExternalLink size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-3 lg:hidden">
            {filteredOrders.map((order, index) => {
              const key = order.orderNumber || index;
              const isOpen = expanded[key];

              return (
                <div
                  key={key}
                  className="overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] p-4 backdrop-blur-xl"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <button
                        type="button"
                        onClick={() => openOrder(order)}
                        className="font-mono text-[11px] text-emerald-300"
                      >
                        #{order.orderNumber}
                      </button>
                      <p className="mt-1 truncate text-sm font-semibold text-white">
                        {order.title || "Untitled"}
                      </p>
                    </div>
                    <StatusBadge status={order.status} />
                  </div>

                  <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-white/40">
                        Price
                      </p>
                      <p className="mt-0.5 font-semibold text-white">
                        {formatPrice(order.price?.initial)}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-white/40">
                        Due
                      </p>
                      <p className="mt-0.5 text-white/80">
                        {order.dueDate || "-"}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-white/40">
                        Days
                      </p>
                      <p className="mt-0.5 text-white/80">
                        {order.daysNeeded ?? "-"}
                      </p>
                    </div>
                  </div>

                  {isOpen && order.description && (
                    <p className="mt-3 rounded-xl border border-white/10 bg-black/30 p-3 text-xs leading-relaxed text-white/70">
                      {order.description}
                    </p>
                  )}

                  <div className="mt-4 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => openOrder(order)}
                      className="flex-1 rounded-xl px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-black"
                      style={{
                        background: "linear-gradient(135deg, #10b981, #34d399)",
                        boxShadow: "0 12px 30px -12px #10b981",
                      }}
                    >
                      View Order
                    </button>
                    {order.description && (
                      <button
                        type="button"
                        onClick={() => toggleExpand(key)}
                        className="grid h-10 w-10 place-items-center rounded-xl border border-white/10 bg-white/5 text-white/60"
                        aria-label={isOpen ? "Collapse order" : "Expand order"}
                      >
                        {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default React.memo(CreatorOrders);
